import { queryOptions } from '@tanstack/react-query'
import { deleteRequest, getRequest, postRequest, putRequest } from 'global/helpers/requests'
import { uniqueId } from 'lodash'
import {
  type BusinessEntity,
  type BusinessEntityFormValues,
  type CarrierLogo,
  type CarrierProfile,
  type CarrierProfileFormValues,
  type Contact,
  type ContactFormValues,
  type Equipment,
  type EquipmentFormValues,
  type Lane,
  type LaneFormValues,
  type Location,
  type Region,
  type RegionFormValues,
} from './CarrierProfile.types'

const PREFIX = 'carrier-management'

export const CARRIER_PROFILE_QUERY_KEY = 'carrier-profile'
export const CARRIER_LOGO_QUERY_KEY = 'carrier-logo'
export const BUSINESS_ENTITIES_QUERY_KEY = 'carrier-business-entities'
export const CONTACTS_QUERY_KEY = 'carrier-contacts'
export const EQUIPMENT_QUERY_KEY = 'carrier-equipment'
export const LANES_QUERY_KEY = 'carrier-lanes'
export const REGIONS_QUERY_KEY = 'carrier-regions'

const getCarrierProfile = async (): Promise<CarrierProfile> =>
  await getRequest<CarrierProfile>('carrier/profile', {}, PREFIX)

export const carrierProfileQueryOptions = queryOptions({
  queryKey: [CARRIER_PROFILE_QUERY_KEY],
  queryFn: getCarrierProfile,
})

export const editCarrierProfile = async (data: CarrierProfileFormValues): Promise<CarrierProfile> =>
  await postRequest<CarrierProfile>('carrier/profile', { body: data }, PREFIX)

const getCarrierLogo = async (): Promise<string> => {
  const response = await getRequest<CarrierLogo>('carrier/logo', {}, PREFIX)
  return `data:${response.contentType};base64,${response.fileData}`
}

export const carrierLogoQueryOptions = queryOptions({
  queryKey: [CARRIER_LOGO_QUERY_KEY],
  queryFn: getCarrierLogo,
})

export const editCarrierLogo = (async (file: File): Promise<string> => {
  const formData = new FormData()
  formData.append('logo', file)
  const response = await postRequest<CarrierLogo>('carrier/logo', { body: formData }, PREFIX)
  return `data:${response.contentType};base64,${response.fileData}`
})

export const deleteCarrierLogo = async (): Promise<void> =>
  await deleteRequest<void>('carrier/logo', {}, PREFIX)

const getBusinessEntities = async (): Promise<BusinessEntity[]> =>
  await getRequest<BusinessEntity[]>('carrier/business-entities', {}, PREFIX)

export const businessEntitiesQueryOptions = queryOptions({
  queryKey: [BUSINESS_ENTITIES_QUERY_KEY],
  queryFn: getBusinessEntities,
})

export const createBusinessEntity = async (data: BusinessEntityFormValues): Promise<BusinessEntity> =>
  await postRequest<BusinessEntity>('carrier/business-entities', { body: data }, PREFIX)

export const editBusinessEntity = async (businessEntityId: string, data: BusinessEntityFormValues): Promise<BusinessEntity> =>
  await putRequest<BusinessEntity>(`carrier/business-entities/${businessEntityId}`, { body: data }, PREFIX)

export const deleteBusinessEntity = async (businessEntityId: string): Promise<void> => {
  await deleteRequest<void>(`carrier/business-entities/${businessEntityId}`, {}, PREFIX)
}

const getContacts = async (): Promise<Contact[]> =>
  await getRequest<Contact[]>('carrier/contacts', {}, PREFIX)

export const contactsQueryOptions = queryOptions({
  queryKey: [CONTACTS_QUERY_KEY],
  queryFn: getContacts,
})

export const createContact = async (data: ContactFormValues): Promise<Contact> =>
  await postRequest<Contact>('carrier/contacts', { body: data }, PREFIX)

export const editContact = async (contactId: string, data: ContactFormValues): Promise<Contact> =>
  await putRequest<Contact>(`carrier/contacts/${contactId}`, { body: data }, PREFIX)

export const deleteContact = async (contactId: string): Promise<void> => {
  await deleteRequest<void>(`carrier/contacts/${contactId}`, {}, PREFIX)
}

const getEquipment = async (): Promise<Equipment[]> =>
  await getRequest<Equipment[]>('carrier/equipment', {}, PREFIX)

export const equipmentQueryOptions = queryOptions({
  queryKey: [EQUIPMENT_QUERY_KEY],
  queryFn: getEquipment,
})

export const createEquipment = async (data: EquipmentFormValues): Promise<Equipment> =>
  await postRequest<Equipment>('carrier/equipment', { body: data }, PREFIX)

export const editEquipment = async (equipmentId: string, data: EquipmentFormValues): Promise<Equipment> =>
  await putRequest<Equipment>(`carrier/equipment/${equipmentId}`, { body: data }, PREFIX)

export const deleteEquipment = async (equipmentId: string): Promise<void> => {
  await deleteRequest<void>(`carrier/equipment/${equipmentId}`, {}, PREFIX)
}

const getLanes = async (): Promise<Lane[]> =>
  (await getRequest<Lane[]>('carrier/lanes', {}, PREFIX)).map((lane: Lane) => ({
    ...lane,
    startLocation: processLocation(lane.startLocation),
    endLocation: processLocation(lane.endLocation),
  }))

export const lanesQueryOptions = queryOptions({
  queryKey: [LANES_QUERY_KEY],
  queryFn: getLanes,
})

export const createLane = async (data: LaneFormValues): Promise<Lane> =>
  await postRequest<Lane>('carrier/lanes', { body: data }, PREFIX)

export const editLane = async (laneId: string, data: LaneFormValues): Promise<Lane> =>
  await putRequest<Lane>(`carrier/lanes/${laneId}`, { body: data }, PREFIX)

export const deleteLane = async (laneId: string): Promise<void> => {
  await deleteRequest<void>(`carrier/lanes/${laneId}`, {}, PREFIX)
}

const getRegions = async (): Promise<Region[]> =>
  (await getRequest<Region[]>('carrier/regions', {}, PREFIX)).map((region: Region) => ({
    ...region,
    location: processLocation(region.location),
  }))

export const regionsQueryOptions = queryOptions({
  queryKey: [REGIONS_QUERY_KEY],
  queryFn: getRegions,
})

export const createRegion = async (data: RegionFormValues): Promise<Region> =>
  await postRequest<Region>('carrier/regions', { body: data }, PREFIX)

export const editRegion = async (regionId: string, data: RegionFormValues): Promise<Region> =>
  await putRequest<Region>(`carrier/regions/${regionId}`, { body: data }, PREFIX)

export const deleteRegion = async (regionId: string): Promise<void> => {
  await deleteRequest<void>(`carrier/regions/${regionId}`, {}, PREFIX)
}

const processLocation = (location: Location) => ({
  ...location,
  areas: location.areas.map((area) => ({
    ...area,
    id: uniqueId('area_'),
  })),
})
